/** One management screen for knowledge collections and for chat channels (the founder, 2026-10-12; DECISIONS "2026-10-12 — One management
 *  screen for collections and channels"): the pure rules the help centre's templates, the articles page and the two management screens
 *  share. No database, no screen. */

/** How a collection lists its articles, for customers in the help centre and for the team on the collection's page: the team's own drag
 *  order, or one of three automatic sorts. A setting of the collection (`kb_collections.sort`, migration 0081), never a filter. */
export type CollectionSort = "custom" | "title" | "newest" | "updated";
export const COLLECTION_SORTS: { id: CollectionSort; label: string }[] = [
  { id: "custom", label: "Custom order" }, { id: "title", label: "Title A–Z" }, { id: "newest", label: "Newest first" }, { id: "updated", label: "Recently updated" },
];
export const DEFAULT_COLLECTION_SORT: CollectionSort = "custom";
export const collectionSortFrom = (v: string | null | undefined): CollectionSort => COLLECTION_SORTS.find((s) => s.id === v)?.id ?? DEFAULT_COLLECTION_SORT;
export const collectionSortLabel = (v: string | null | undefined): string => COLLECTION_SORTS.find((s) => s.id === collectionSortFrom(v))!.label;

/** an article as far as the sorts read it: its place (custom), its title (title), its creation (newest), its words' last change (updated) */
export type SortableArticle = { id: string; title: string; position?: number; createdAt?: string; contentUpdatedAt?: string };
const byId = (a: SortableArticle, b: SortableArticle) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);
const latestFirst = (key: "createdAt" | "contentUpdatedAt") => (a: SortableArticle, b: SortableArticle) => (b[key] ?? "").localeCompare(a[key] ?? "");
const COMPARE: Record<CollectionSort, (a: SortableArticle, b: SortableArticle) => number> = {
  custom: (a, b) => (a.position ?? 0) - (b.position ?? 0),
  title: (a, b) => a.title.localeCompare(b.title, "en", { sensitivity: "base" }),
  newest: latestFirst("createdAt"),
  updated: latestFirst("contentUpdatedAt"),
};
/** A collection's articles in its sort, ties by id; a sort it does not know is the custom order. A new array: the input stays as it was. */
export function sortCollectionArticles<T extends SortableArticle>(rows: T[], sort: string | null | undefined): T[] {
  const compare = COMPARE[collectionSortFrom(sort)];
  return [...rows].sort((a, b) => compare(a, b) || byId(a, b));
}

/** A drag's result as the full order the server stores. `ids` is the full order, `visible` the ids on screen in that order (a filter may
 *  hide some), `toIndex` the place among the visible rows where the dragged row lands: just before the visible row now at `toIndex` when it
 *  moves up, just after it when it moves down; hidden rows keep their places. Null when nothing changes or the row is not on screen. */
export function dropAmong(ids: string[], visible: string[], id: string, toIndex: number): string[] | null {
  const from = visible.indexOf(id);
  const shown = visible.filter((x) => ids.includes(x));
  if (from < 0 || !ids.includes(id) || shown.length !== visible.length) return null;
  const to = Math.max(0, Math.min(visible.length - 1, toIndex));
  if (to === from) return null;
  const other = visible[to]!;
  const rest = ids.filter((x) => x !== id);
  const at = rest.indexOf(other) + (to > from ? 1 : 0);
  return [...rest.slice(0, at), id, ...rest.slice(at)];
}
